import React from 'react';
import { motion } from 'framer-motion';
import { Clock, FileText, Calendar, Hash, Trash2, ChevronRight } from 'lucide-react';
import { formatKenmerk } from '../utils/api';

// Utility functions for localStorage
export const getRecentDecodes = () => {
  try {
    const stored = localStorage.getItem('recentDecodes');
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

export const saveRecentDecode = (result) => {
  if (!result || !result.valid) return;
  
  try {
    const recents = getRecentDecodes();
    
    // Create entry with masked kenmerk (first 4 + **** + last 4)
    const kenmerk = result.kenmerk || '';
    const maskedKenmerk = kenmerk.length === 16 
      ? `${kenmerk.slice(0, 4)} **** **** ${kenmerk.slice(-4)}`
      : kenmerk;
    
    const newEntry = {
      id: Date.now(),
      maskedKenmerk,
      fullKenmerk: kenmerk, // For re-decoding
      belastingtype: result.belastingtype?.nl || 'Onbekend',
      jaar: result.jaar,
      tijdvak: result.tijdvak,
      timestamp: new Date().toISOString()
    };
    
    // Remove duplicate if exists (same kenmerk)
    const filtered = recents.filter(r => r.fullKenmerk !== kenmerk);
    
    // Add to front, keep max 5
    const updated = [newEntry, ...filtered].slice(0, 5);
    
    localStorage.setItem('recentDecodes', JSON.stringify(updated));
    return updated;
  } catch {
    return [];
  }
};

export const clearRecentDecodes = () => {
  try {
    localStorage.removeItem('recentDecodes');
    return [];
  } catch {
    return [];
  }
};

const RecentDecodes = ({ onSelect, language = 'nl' }) => {
  const [recents, setRecents] = React.useState([]);
  
  React.useEffect(() => {
    setRecents(getRecentDecodes());
    
    // Listen for storage changes (cross-tab sync)
    const handleStorage = () => setRecents(getRecentDecodes());
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);
  
  // Refresh recents when component is visible
  React.useEffect(() => {
    const interval = setInterval(() => {
      setRecents(getRecentDecodes());
    }, 1000);
    return () => clearInterval(interval);
  }, []);
  
  if (recents.length === 0) return null;
  
  const handleClear = () => {
    clearRecentDecodes();
    setRecents([]);
  };
  
  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return language === 'nl' ? 'Zojuist' : 'Just now';
    if (diffMins < 60) return language === 'nl' ? `${diffMins} min geleden` : `${diffMins} min ago`;
    if (diffHours < 24) return language === 'nl' ? `${diffHours} uur geleden` : `${diffHours}h ago`;
    if (diffDays < 7) return language === 'nl' ? `${diffDays} dagen geleden` : `${diffDays}d ago`;
    
    return date.toLocaleDateString(language === 'nl' ? 'nl-NL' : 'en-US', { 
      day: 'numeric', 
      month: 'short' 
    });
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="mt-12 max-w-2xl mx-auto"
      data-testid="recent-decodes-section"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
          <Clock className="w-4 h-4" />
          <span className="text-sm font-medium">
            {language === 'nl' ? 'Recente decoderingen' : 'Recent decodes'}
          </span>
        </div>
        <button
          onClick={handleClear}
          className="flex items-center gap-1 text-xs text-slate-400 hover:text-error transition-colors"
          data-testid="clear-recent-btn"
        >
          <Trash2 className="w-3 h-3" />
          {language === 'nl' ? 'Wissen' : 'Clear'}
        </button>
      </div>
      
      <div className="space-y-2">
        {recents.map((item, index) => (
          <motion.button
            key={item.id}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.05 }}
            onClick={() => onSelect(item.fullKenmerk)}
            className="w-full group flex items-center gap-4 p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 hover:bg-white dark:hover:bg-slate-800 border border-transparent hover:border-slate-200 dark:hover:border-slate-700 hover:shadow-md transition-all text-left"
            data-testid={`recent-item-${index}`}
          >
            {/* Tax Type Icon */}
            <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
              <FileText className="w-5 h-5 text-accent" />
            </div>
            
            {/* Content */}
            <div className="flex-grow min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-medium text-slate-900 dark:text-white truncate">
                  {item.belastingtype}
                </span>
                {item.tijdvak && item.tijdvak !== 'Onbekend' && (
                  <span className="px-2 py-0.5 text-xs rounded-full bg-accent/10 text-accent">
                    {item.tijdvak}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3 mt-1 text-xs text-slate-500 dark:text-slate-400">
                <span className="flex items-center gap-1 font-mono">
                  <Hash className="w-3 h-3" />
                  {item.maskedKenmerk}
                </span>
                {item.jaar && (
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {item.jaar}
                  </span>
                )}
              </div>
            </div>
            
            {/* Time & Arrow */}
            <div className="flex-shrink-0 flex items-center gap-2">
              <span className="text-xs text-slate-400">
                {formatTime(item.timestamp)}
              </span>
              <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-accent transition-colors" />
            </div>
          </motion.button>
        ))}
      </div>
    </motion.div>
  );
};

export default RecentDecodes;
